import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, Stack, useMantineTheme } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import {
  AsyncDisplay,
  CardV2 as Card,
  Checkbox,
  DebouncedInput,
  EmptyState,
  Heading,
  Input,
  SegmentedControl,
  Text,
} from '@feathr/components';

import { useSearch } from './Interests.useSearch';
import { useSelectedInterests } from './Interests.useSelectedInterests';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function Interests({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const {
    isSearching,
    filteredInterests,
    categories,
    selectedCategory,
    handleSearch,
    handleCategoryChange,
    search,
    isLoading,
  } = useSearch();
  const { handleInterestChange, selectedInterestIds } = useSelectedInterests({
    campaign,
    targetings,
    filteredInterests,
  });

  const numSearchResults = filteredInterests.length;
  const isNotSearching = !isSearching && !search;
  const hasResults = numSearchResults > 0 && search && !isSearching;
  const hasNoResults = numSearchResults === 0 && search && !isSearching;

  // Animation variants for stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 8,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <Card width={'full'}>
      <Card.Header isCollapsible={true} title={t('Interests')} />
      <Card.Content addVerticalGap={true} isLoading={isLoading}>
        <DebouncedInput<string> defaultValue={search} onChange={handleSearch}>
          {(liveValue, onChangeLiveValue): JSX.Element => (
            <Input
              isClearable={true}
              onChange={onChangeLiveValue}
              placeholder={t('Search by keyword, interest, or audience name')}
              prefix={<FontAwesomeIcon icon={faSearch} />}
              type={'text'}
              value={liveValue}
            />
          )}
        </DebouncedInput>

        {isNotSearching && (
          <>
            <motion.div animate={'visible'} initial={'hidden'} key={'segmented-control'}>
              <SegmentedControl
                data={[
                  { label: t('Suggested'), value: 'suggested' },
                  ...categories.map((category) => ({
                    label: category,
                    value: category,
                  })),
                ]}
                onChange={handleCategoryChange}
                value={selectedCategory}
              />
            </motion.div>
          </>
        )}

        {(hasNoResults || hasResults) && (
          <Group justify={'space-between'}>
            {hasResults && (
              <Heading order={3} variant={'h5'}>
                {t('{{count}} result found', { count: numSearchResults })}
              </Heading>
            )}

            {hasNoResults && (
              <EmptyState
                description={t(
                  'Try checking your spelling, using broader terms, or searching by category.',
                )}
                label={t('No results found for "{{search}}"', { search })}
                theme={'slate'}
                width={'full'}
              />
            )}
          </Group>
        )}

        {filteredInterests && filteredInterests.length > 0 ? (
          <AsyncDisplay isLoading={isSearching}>
            <motion.div
              animate={'visible'}
              initial={'hidden'}
              key={`interests-${filteredInterests.length}-${search}`}
              variants={containerVariants}
            >
              <Stack gap={theme.spacing['2']}>
                {filteredInterests.map((interest, index) => {
                  const isSelected = selectedInterestIds.includes(interest.id);
                  const interestData = interest.get('interest');
                  return (
                    interestData && (
                      <motion.div custom={index} key={interest.id} variants={itemVariants}>
                        <Checkbox
                          helpPlacement={'bottom'}
                          label={
                            <Stack gap={0}>
                              <Text.DefaultMedium>{interestData.name}</Text.DefaultMedium>
                              <Text.DefaultRegular>{interestData.description}</Text.DefaultRegular>
                            </Stack>
                          }
                          layout={'well'}
                          name={'interests'}
                          onChange={(checked): void => handleInterestChange(checked, interest.id)}
                          value={isSelected}
                        />
                      </motion.div>
                    )
                  );
                })}
              </Stack>
            </motion.div>
          </AsyncDisplay>
        ) : null}
      </Card.Content>
    </Card>
  );
}

export default observer(Interests);
